
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AddonService, ClientAddonServices, AddonServiceConfig } from '../types/addon.types';
import { useAuth } from './AuthContext';
import { useClient } from './ClientContext';
import { API_BASE_URL } from '../constants';

const AVAILABLE_ADDONS: AddonService[] = [
  {
    id: 'whatsapp',
    name: 'WhatsApp Business',
    description: 'Connect your WhatsApp Business Account to enable AI-powered chatbot, broadcasts, templates, and agent management',
    icon: '💬',
    category: 'communication',
    isEnabled: false,
    setupComplete: false,
    features: [
      'AI-powered WhatsApp chatbot',
      'Custom AI persona configuration',
      'Broadcast messaging to contacts',
      'Message template management',
      'Agent trigger automation',
      'Agent performance reports',
      'Real-time conversation handling'
    ],
    requirements: [
      'Facebook Business Account',
      'WhatsApp Business Account (WABA)',
      'Meta Business verification'
    ],
    supportedActions: [
      'send_message',
      'send_template',
      'manage_chatbot',
      'configure_triggers',
      'view_analytics'
    ],
    pricing: {
      tier: 'free',
      monthlyPrice: 0
    }
  },
  {
    id: 'zoho_crm',
    name: 'Zoho CRM',
    description: 'Automatically capture leads from WhatsApp conversations into Zoho CRM',
    icon: '📊',
    category: 'crm',
    isEnabled: false,
    setupComplete: false,
    features: [
      'Automatic lead capture from WhatsApp',
      'AI-powered conversation analysis',
      'Custom field mapping',
      'Real-time synchronization',
      'Lead scoring and qualification'
    ],
    requirements: [
      'Zoho CRM account',
      'Administrator access',
      'API permissions enabled'
    ],
    supportedActions: [
      'capture_lead',
      'update_lead',
      'search_leads',
      'create_contact'
    ],
    pricing: {
      tier: 'free',
      monthlyPrice: 0
    }
  },
  {
    id: 'google_calendar',
    name: 'Google Calendar',
    description: 'Enable appointment booking through WhatsApp with automatic Google Calendar integration',
    icon: '📅',
    category: 'productivity',
    isEnabled: false,
    setupComplete: false,
    features: [
      'Appointment booking via WhatsApp',
      'AI-powered availability checking',
      'Automatic calendar event creation',
      'Multiple appointment types',
      'Business hours configuration',
      'Email confirmations',
      'Customer self-service booking'
    ],
    requirements: [
      'Google account with Calendar access',
      'OAuth 2.0 authentication',
      'Calendar API permissions'
    ],
    supportedActions: [
      'check_availability',
      'book_appointment',
      'cancel_appointment',
      'reschedule_appointment',
      'list_appointments'
    ],
    pricing: {
      tier: 'free',
      monthlyPrice: 0
    }
  },
  {
    id: 'leads',
    name: 'CRM',
    description: 'A built-in CRM to manage your contacts, track deals through your pipeline, and automate lead capture directly from WhatsApp conversations',
    icon: '🎯',
    category: 'crm',
    isEnabled: false,
    setupComplete: false,
    features: [
      'Contact and lead management',
      'Sales pipeline tracking',
      'AI-powered lead capture from conversations',
      'Custom AI trigger configuration',
      'Deal status and stage tracking',
      'Contact source attribution',
      'Conversation context capture',
      'CRM analytics and reporting'
    ],
    requirements: [
      'WhatsApp Business Account connected',
      'AI triggers enabled'
    ],
    supportedActions: [
      'create_lead',
      'update_lead',
      'delete_lead',
      'search_leads',
      'capture_lead_ai',
      'view_analytics'
    ],
    pricing: {
      tier: 'free',
      monthlyPrice: 0
    }
  },
  {
    id: 'xero',
    name: 'Xero Accounting',
    description: 'Automatically create draft invoices in Xero from WhatsApp orders with AI-powered order detail extraction',
    icon: '📊',
    category: 'accounting',
    isEnabled: false,
    setupComplete: false,
    features: [
      'Automatic draft invoice creation from WhatsApp',
      'AI-powered order detail extraction',
      'Automated contact management in Xero',
      'Business onboarding and customer registration',
      'Real-time synchronization',
      'Support for line items and totals',
      'Custom invoice references',
      'Multi-currency support'
    ],
    requirements: [
      'Xero account (any plan)',
      'Administrator or Standard user access',
      'OAuth 2.0 authentication'
    ],
    supportedActions: [
      'save_to_xero',
      'business_onboarding',
      'create_invoice',
      'create_contact',
      'update_invoice'
    ],
    pricing: {
      tier: 'free',
      monthlyPrice: 0
    }
  }
];

interface AddonContextType {
  addons: AddonService[];
  enabledAddons: AddonService[];
  clientAddonConfig: ClientAddonServices;
  loading: boolean;
  error: string | null;
  getAddon: (addonId: string) => AddonService | undefined;
  getAddonConfig: (addonId: string) => AddonServiceConfig | undefined;
  isAddonReady: (addonId: string) => boolean;
  toggleAddon: (addonId: string, enabled: boolean) => Promise<void>;
  installAddon: (addonId: string) => Promise<void>;
  uninstallAddon: (addonId: string) => Promise<void>;
  getAddonsByCategory: (category: string) => AddonService[];
  searchAddons: (query: string) => AddonService[];
  getAddonStats: () => {
    total: number;
    enabled: number;
    configured: number;
    byCategory: Record<string, number>;
  };
  refreshConfig: () => Promise<void>;
}

const AddonContext = createContext<AddonContextType | undefined>(undefined);

export const AddonProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentUser, idToken, getValidIdToken, isLoading: isAuthLoading } = useAuth();
  const { selectedClientId, isLoading: isClientLoading } = useClient();
  const [addons, setAddons] = useState<AddonService[]>(AVAILABLE_ADDONS);
  const [clientAddonConfig, setClientAddonConfig] = useState<ClientAddonServices>({});
  const [loading, setLoading] = useState(true); // Start loading true
  const [error, setError] = useState<string | null>(null);

  // Fetch client addon configuration
  const fetchAddonConfig = useCallback(async () => {
    // If auth is still loading or client context is initializing, do nothing yet
    if (isAuthLoading || isClientLoading) return;

    if (!currentUser) {
      console.log('No currentUser available to fetch addon config');
      setLoading(false);
      return;
    }

    // If no client is selected, we cannot fetch configuration
    if (!selectedClientId) {
      setClientAddonConfig({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const executeRequest = async (token: string) => {
        const headers: Record<string, string> = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        
        if (selectedClientId) {
          headers['X-Client-ID'] = selectedClientId;
        }
        
        return fetch(`${API_BASE_URL}/client/config`, {
          headers
        });
      };

      let tokenToUse = idToken;
      if (!tokenToUse) {
        tokenToUse = await getValidIdToken();
      }

      if (!tokenToUse) {
        setError('Unable to authenticate. Please log in again.');
        setLoading(false);
        return;
      }

      console.log('Fetching addon config for user:', currentUser.uid);

      let response = await executeRequest(tokenToUse);

      if (response.status === 401) {
        console.warn('Addon config fetch returned 401. Attempting token refresh.');
        const refreshedToken = await getValidIdToken(true);
        if (!refreshedToken) {
          setError('Session expired. Please log in again.');
          setLoading(false);
          return;
        }
        response = await executeRequest(refreshedToken);
      }

      console.log('Response status:', response.status);

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          console.log('Received addon config data:', data);
          
          const addonServices = data.addonServices || {};
          setClientAddonConfig(addonServices);

          // Update addon status based on client config
          setAddons(prev => prev.map(addon => ({
            ...addon,
            isEnabled: addonServices[addon.id]?.isEnabled || false,
            setupComplete: addonServices[addon.id]?.setupComplete || false,
            setupDate: addonServices[addon.id]?.setupDate,
            lastSyncDate: addonServices[addon.id]?.lastSyncDate
          })));
        } else {
          const responseText = await response.text();
          console.error('Non-JSON response from /api/client/config:', responseText);
          setError('Server returned invalid response format');
        }
      } else {
        const responseText = await response.text();
        console.error('API Error Response:', response.status, responseText);
        if (response.status === 401) {
          setError('Unable to authenticate with the server. Please sign in again.');
        } else {
          setError(`Failed to load addon configuration (${response.status})`);
        }
      }
    } catch (err) {
      console.error('Error fetching addon config:', err);
      setError('Failed to load addon configuration');
    } finally {
      setLoading(false);
    }
  }, [currentUser, idToken, getValidIdToken, isAuthLoading, isClientLoading, selectedClientId]);

  // Initial fetch
  useEffect(() => {
    if (!isAuthLoading && !isClientLoading) {
        fetchAddonConfig();
    }
  }, [fetchAddonConfig, isAuthLoading, isClientLoading]);


  // Get enabled addons
  const enabledAddons = addons.filter(addon => addon.isEnabled);

  // Get addon by ID
  const getAddon = useCallback((addonId: string): AddonService | undefined => {
    return addons.find(addon => addon.id === addonId);
  }, [addons]);

  // Get addon configuration
  const getAddonConfig = useCallback((addonId: string): AddonServiceConfig | undefined => {
    return clientAddonConfig[addonId as keyof ClientAddonServices];
  }, [clientAddonConfig]);

  // Check if addon is installed and configured
  const isAddonReady = useCallback((addonId: string): boolean => {
    const addon = getAddon(addonId);
    const config = getAddonConfig(addonId);
    
    // Addons that don't require external authentication (no OAuth tokens needed)
    const noAuthRequired = ['leads', 'whatsapp'];
    
    if (noAuthRequired.includes(addonId)) {
      // For addons without external auth, just check enabled and setup complete
      return !!(addon?.isEnabled && addon?.setupComplete);
    }
    
    // For addons with external auth (zoho_crm, google_calendar), check tokens too
    return !!(addon?.isEnabled && addon?.setupComplete && config?.authentication?.hasValidTokens);
  }, [getAddon, getAddonConfig]);

  // Enable/disable addon
  const toggleAddon = useCallback(async (addonId: string, enabled: boolean) => {
    if (!currentUser) return;

    try {
      setLoading(true);
      
      // Update local state immediately for better UX
      setAddons(prev => prev.map(addon => 
        addon.id === addonId ? { ...addon, isEnabled: enabled } : addon
      ));

      const performToggle = async (token: string) => {
        const headers: Record<string, string> = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        if (selectedClientId) {
          headers['X-Client-ID'] = selectedClientId;
        }
        return fetch(`${API_BASE_URL}/client/addons/${addonId}/toggle`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ enabled })
        });
      };

      let tokenToUse = idToken;
      if (!tokenToUse) {
        tokenToUse = await getValidIdToken();
      }

      if (!tokenToUse) {
        throw new Error('UNAUTHENTICATED');
      }

      let response = await performToggle(tokenToUse);

      if (response.status === 401) {
        console.warn('Toggle addon request returned 401. Attempting token refresh.');
        const refreshedToken = await getValidIdToken(true);
        if (!refreshedToken) {
          throw new Error('UNAUTHENTICATED');
        }
        response = await performToggle(refreshedToken);
      }

      if (!response.ok) {
        throw new Error('Failed to toggle addon');
      }

      // Refresh configuration
      await fetchAddonConfig();
    } catch (err) {
      console.error('Error toggling addon:', err);
      if (err instanceof Error && err.message === 'UNAUTHENTICATED') {
        setError('Unable to authenticate. Please sign in again.');
      } else {
        setError('Failed to toggle addon');
      }
      
      // Revert local state on error
      setAddons(prev => prev.map(addon => 
        addon.id === addonId ? { ...addon, isEnabled: !enabled } : addon
      ));
    } finally {
      setLoading(false);
    }
  }, [currentUser, idToken, getValidIdToken, fetchAddonConfig]);

  // Install addon
  const installAddon = useCallback(async (addonId: string) => {
    await toggleAddon(addonId, true);
  }, [toggleAddon]);

  // Uninstall addon
  const uninstallAddon = useCallback(async (addonId: string) => {
    await toggleAddon(addonId, false);
  }, [toggleAddon]);

  // Get addons by category
  const getAddonsByCategory = useCallback((category: string) => {
    return addons.filter(addon => addon.category === category);
  }, [addons]);

  // Search addons
  const searchAddons = useCallback((query: string) => {
    const lowercaseQuery = query.toLowerCase();
    return addons.filter(addon => 
      addon.name.toLowerCase().includes(lowercaseQuery) ||
      addon.description.toLowerCase().includes(lowercaseQuery) ||
      addon.features.some(feature => feature.toLowerCase().includes(lowercaseQuery))
    );
  }, [addons]);

  // Get addon statistics
  const getAddonStats = useCallback(() => {
    return {
      total: addons.length,
      enabled: enabledAddons.length,
      configured: addons.filter(addon => addon.setupComplete).length,
      byCategory: addons.reduce((acc, addon) => {
        acc[addon.category] = (acc[addon.category] || 0) + 1;
        return acc;
      }, {} as Record<string, number>)
    };
  }, [addons, enabledAddons]);

  return (
    <AddonContext.Provider value={{
      addons,
      enabledAddons,
      clientAddonConfig,
      loading,
      error,
      getAddon,
      getAddonConfig,
      isAddonReady,
      toggleAddon,
      installAddon,
      uninstallAddon,
      getAddonsByCategory,
      searchAddons,
      getAddonStats,
      refreshConfig: fetchAddonConfig
    }}>
      {children}
    </AddonContext.Provider>
  );
};

export const useAddonContext = () => {
  const context = useContext(AddonContext);
  if (context === undefined) {
    throw new Error('useAddonContext must be used within an AddonProvider');
  }
  return context;
};
